import { GetTypingRateColor } from "./QuickJournalTypingRateHelpers";

export function FocusTextAreaWithoutScrolling(
    textArea: HTMLTextAreaElement | null
) {
    if (!textArea) {
        return;
    }

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    try {
        textArea.focus({ preventScroll: true });
    } catch {
        textArea.focus();
    }

    window.scrollTo(scrollX, scrollY);
}

export function GetQuickJournalCursorViewportLeft(): number {
    if (typeof window === "undefined") {
        return 0;
    }

    return window.innerWidth / 2;
}

export function GetCursorColor(charactersPerSecond: number): string {
    return GetTypingRateColor(charactersPerSecond);
}

interface ICaretMirror {
    element: HTMLDivElement;
    textNode: Text;
    caretSpan: HTMLSpanElement;
    copiedStyles: Map<string, string>;
    resizeObserver?: ResizeObserver;
}

const caretMirrors = new WeakMap<HTMLTextAreaElement, ICaretMirror>();

function CopyCaretMirrorStyles(
    textarea: HTMLTextAreaElement,
    mirror: ICaretMirror
) {
    const computedStyle = getComputedStyle(textarea);
    const nextStyles = new Map<string, string>();

    // Copy text geometry only. Copying transforms and positioning made the
    // hidden mirror change page overflow as the iOS caret moved.
    const properties = ["box-sizing", "width", "height", "font-family", "font-size",
        "font-style", "font-weight", "font-stretch", "font-variant", "line-height",
        "letter-spacing", "word-spacing", "text-indent", "text-align", "text-transform",
        "direction", "tab-size", "padding-top", "padding-right", "padding-bottom", "padding-left",
        "border-top-width", "border-right-width", "border-bottom-width", "border-left-width", "border-style"];
    for (const property of properties) {
        const value = computedStyle.getPropertyValue(property);
        nextStyles.set(property, value);

        if (mirror.copiedStyles.get(property) !== value) {
            mirror.element.style.setProperty(property, value);
        }
    }

    mirror.copiedStyles.forEach((_, property) => {
        if (!nextStyles.has(property)) {
            mirror.element.style.removeProperty(property);
        }
    });

    mirror.copiedStyles = nextStyles;
    mirror.element.style.position = "fixed";
    mirror.element.style.top = "0";
    mirror.element.style.left = "0";
    mirror.element.style.pointerEvents = "none";
    mirror.element.style.contain = "layout paint";
    mirror.element.style.visibility = "hidden";
    mirror.element.style.whiteSpace = "pre-wrap";
    mirror.element.style.wordWrap = "break-word";
}

function GetOrCreateCaretMirror(textarea: HTMLTextAreaElement): ICaretMirror {
    const existingMirror = caretMirrors.get(textarea);
    if (existingMirror) {
        return existingMirror;
    }

    const element = document.createElement("div");
    const textNode = document.createTextNode("");
    const caretSpan = document.createElement("span");
    const mirror: ICaretMirror = {
        element,
        textNode,
        caretSpan,
        copiedStyles: new Map<string, string>(),
    };

    element.dataset.quickJournalCaretMirror = "true";
    element.setAttribute("aria-hidden", "true");
    element.appendChild(textNode);
    element.appendChild(caretSpan);
    document.body.appendChild(element);
    CopyCaretMirrorStyles(textarea, mirror);

    if (typeof ResizeObserver !== "undefined") {
        mirror.resizeObserver = new ResizeObserver(() =>
            CopyCaretMirrorStyles(textarea, mirror)
        );
        mirror.resizeObserver.observe(textarea);
    }

    caretMirrors.set(textarea, mirror);
    return mirror;
}

export function RefreshCaretCoordinatesMirror(
    textarea: HTMLTextAreaElement | null
) {
    if (!textarea) {
        return;
    }

    const mirror = caretMirrors.get(textarea);
    if (mirror) {
        CopyCaretMirrorStyles(textarea, mirror);
    }
}

export function RemoveCaretCoordinatesMirror(
    textarea: HTMLTextAreaElement | null
) {
    if (!textarea) {
        return;
    }

    const mirror = caretMirrors.get(textarea);
    if (!mirror) {
        return;
    }

    mirror.resizeObserver?.disconnect();
    mirror.element.remove();
    caretMirrors.delete(textarea);
}

export function GetCaretCoordinates(
    textarea: HTMLTextAreaElement,
    position: number
): { left: number; top: number } {
    const mirror = GetOrCreateCaretMirror(textarea);

    const textBeforeCaret = textarea.value.substring(0, position);
    mirror.textNode.nodeValue = textBeforeCaret;
    mirror.caretSpan.textContent = textarea.value.substring(position) || ".";

    const divRect = mirror.element.getBoundingClientRect();
    const spanRect = mirror.caretSpan.getBoundingClientRect();

    return {
        left: spanRect.left - divRect.left,
        top: spanRect.top - divRect.top,
    };
}
